/**
 * ContributorPortal
 *
 * Modal dialog for field-worker Catalyst submission.
 * Submits a CatalystCreateRequest to POST /api/catalysts.
 *
 * Required fields: geofield (WKT POINT), impact_count, event_date, organization_id
 * Optional: narrative, security_level, event_type, story, lives_affected, region
 *
 * Steps:
 *   0 Location  — drop a pin → lat/lng + region name
 *   1 Details   — impact count, date, event type, security level, org ID
 *   2 Narrative — narrative text + personal story/testimony
 *   3 Review    — summary before submit
 */
import { useState, useRef } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useSubmitEvent, toWkt } from '@/hooks/useSubmitEvent';
import { useOrganisations } from '@/hooks/useOrganisations';
import LocationPicker from './LocationPicker';
import { cn } from '@/lib/utils';

const EVENT_TYPES = [
  'Church Service',
  'One-on-One',
  'Small Group',
  'Community Outreach',
  'Youth Event',
  'Prayer Meeting',
  'Workshop / Training',
  'Medical / Relief',
  'Online Event',
];

const SECURITY_LEVELS = [
  { value: 'public',     label: 'Public',      desc: 'Precise coordinates and narrative fully visible.' },
  { value: 'obfuscated', label: 'Obfuscated',   desc: 'Coordinates fuzzed ~10 km — protects sensitive regions.' },
  { value: 'private',    label: 'Private',      desc: 'Coordinates hidden; narrative encrypted at rest.' },
];

const STEPS = ['Location', 'Details', 'Narrative', 'Review'];

const initialForm = {
  // Step 0
  lat: '',
  lng: '',
  region: '',
  // Step 1
  impact_count: '',
  lives_affected: '',
  event_date: '',
  event_type: '',
  security_level: 'public',
  organization_id: '',
  // Step 2
  narrative: '',
  story: '',
};

export default function ContributorPortal() {
  const { isContributorPortalOpen, closeContributorPortal } = useUIStore();
  const submitEvent = useSubmitEvent();
  const { data: organisations = [], isLoading: orgsLoading } = useOrganisations();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);

  if (!isContributorPortalOpen) return null;

  const setField = (name, value) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  const handleLocationPick = ({ lat, lng }) => {
    setField('lat', lat);
    setField('lng', lng);
  };

  const canProceed = () => {
    if (step === 0) return form.lat !== '' && form.lng !== '';
    if (step === 1)
      return (
        form.impact_count !== '' &&
        Number(form.impact_count) >= 0 &&
        form.event_date !== '' &&
        form.organization_id !== ''
      );
    return true;
  };

  const handleSubmit = async () => {
    const payload = {
      geofield: toWkt(parseFloat(form.lng), parseFloat(form.lat)),
      impact_count: Number(form.impact_count),
      event_date: form.event_date,
      organization_id: Number(form.organization_id),
    };

    if (form.region) payload.region = form.region;
    if (form.narrative) payload.narrative = form.narrative;
    if (form.story) payload.story = form.story;
    if (form.lives_affected !== '') payload.lives_affected = Number(form.lives_affected);
    if (form.event_type) payload.event_type = form.event_type;
    payload.security_level = form.security_level;

    try {
      await submitEvent.mutateAsync(payload);
      closeContributorPortal();
      setForm(initialForm);
      setStep(0);
    } catch {
      // Error toast shown by useSubmitEvent
    }
  };

  const handleClose = () => {
    closeContributorPortal();
    setForm(initialForm);
    setStep(0);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-lg rounded-2xl border border-neutral-200 bg-document shadow-2xl flex flex-col max-h-[90vh]">

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4 shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-ink">Submit Catalyst</h2>
            <p className="text-xs text-neutral-500">Report a new field event</p>
          </div>
          <button
            onClick={handleClose}
            className="rounded-lg p-1.5 text-neutral-500 hover:bg-parchment hover:text-ink"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Step indicator ──────────────────────────────────────────────────── */}
        <div className="flex border-b border-neutral-200 px-6 shrink-0">
          {STEPS.map((label, i) => (
            <button
              key={label}
              onClick={() => i < step && setStep(i)}
              className={cn(
                'mr-1 border-b-2 py-3 text-xs font-medium transition',
                i === step
                  ? 'border-[#003cc5] text-[#003cc5]'
                  : i < step
                  ? 'border-transparent text-neutral-500 hover:text-ink cursor-pointer'
                  : 'border-transparent text-neutral-400 cursor-default'
              )}
            >
              <span className={cn(
                'mr-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px]',
                i === step ? 'bg-[#003cc5] text-parchment' : i < step ? 'bg-emerald-50 text-emerald-700' : 'bg-neutral-200 text-neutral-500'
              )}>
                {i < step ? '✓' : i + 1}
              </span>
              {label}
            </button>
          ))}
        </div>

        {/* ── Step content ─────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* Step 0: Location */}
          {step === 0 && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-600">
                Drop a pin on the map to set the event location.
              </p>
              <div className="h-64 overflow-hidden rounded-xl border border-neutral-200">
                <LocationPicker
                  onLocationPick={handleLocationPick}
                  initialCoords={form.lat ? [parseFloat(form.lng), parseFloat(form.lat)] : null}
                />
              </div>

              {form.lat && (
                <div className="rounded-lg bg-parchment px-4 py-2.5 text-xs text-ink font-mono border border-neutral-200">
                  📍 {parseFloat(form.lat).toFixed(6)}, {parseFloat(form.lng).toFixed(6)}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-xs font-medium text-neutral-600">
                  Region name <span className="text-neutral-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={form.region}
                  onChange={(e) => setField('region', e.target.value)}
                  placeholder="e.g. East Africa, Southeast Asia"
                  maxLength={255}
                  className="w-full rounded-lg border border-neutral-200 bg-parchment px-3 py-2 text-sm text-ink placeholder:text-neutral-400 focus:border-[#003cc5] focus:outline-none"
                />
                <p className="mt-1 text-xs text-neutral-400">
                  Used for timeline filtering — enter a named geographic region.
                </p>
              </div>
            </div>
          )}

          {/* Step 1: Details */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-neutral-600">
                    Impact count <span className="text-[#003cc5]">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.impact_count}
                    onChange={(e) => setField('impact_count', e.target.value)}
                    placeholder="0"
                    className="w-full rounded-lg border border-neutral-200 bg-parchment px-3 py-2 text-sm text-ink placeholder:text-neutral-400 focus:border-[#003cc5] focus:outline-none"
                  />
                  <p className="mt-1 text-xs text-neutral-400">People directly impacted</p>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-neutral-600">
                    Lives affected <span className="text-neutral-400">(optional)</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.lives_affected}
                    onChange={(e) => setField('lives_affected', e.target.value)}
                    placeholder="0"
                    className="w-full rounded-lg border border-neutral-200 bg-parchment px-3 py-2 text-sm text-ink placeholder:text-neutral-400 focus:border-[#003cc5] focus:outline-none"
                  />
                  <p className="mt-1 text-xs text-neutral-400">Broader reach</p>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-neutral-600">
                  Event date <span className="text-[#003cc5]">*</span>
                </label>
                <input
                  type="date"
                  value={form.event_date}
                  onChange={(e) => setField('event_date', e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  className="w-full rounded-lg border border-neutral-200 bg-parchment px-3 py-2 text-sm text-ink focus:border-[#003cc5] focus:outline-none"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-neutral-600">
                  Event type
                </label>
                <select
                  value={form.event_type}
                  onChange={(e) => setField('event_type', e.target.value)}
                  className="w-full rounded-lg border border-neutral-200 bg-parchment px-3 py-2 text-sm text-ink focus:border-[#003cc5] focus:outline-none"
                >
                  <option value="">— Select type —</option>
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-neutral-600">
                  Organisation <span className="text-[#003cc5]">*</span>
                </label>
                {orgsLoading ? (
                  <div className="h-9 animate-skeleton rounded-lg bg-paper" />
                ) : organisations.length === 0 ? (
                  <p className="rounded-lg border border-neutral-200 bg-parchment px-3 py-2 text-sm text-neutral-400">
                    No organisations found. Ask your administrator to add you to a group.
                  </p>
                ) : (
                  <select
                    value={form.organization_id}
                    onChange={(e) => setField('organization_id', e.target.value)}
                    className="w-full rounded-lg border border-neutral-200 bg-parchment px-3 py-2 text-sm text-ink focus:border-[#003cc5] focus:outline-none"
                  >
                    <option value="">— Select organisation —</option>
                    {organisations.map((org) => (
                      <option key={org.id} value={org.id}>{org.label}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Security level */}
              <div>
                <label className="mb-2 block text-xs font-medium text-neutral-600">
                  Security level
                </label>
                <div className="space-y-2">
                  {SECURITY_LEVELS.map(({ value, label, desc }) => (
                    <label
                      key={value}
                      className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition',
                        form.security_level === value
                          ? 'border-[#003cc5]/40 bg-red-50'
                          : 'border-neutral-200 hover:border-neutral-400'
                      )}
                    >
                      <input
                        type="radio"
                        name="security_level"
                        value={value}
                        checked={form.security_level === value}
                        onChange={() => setField('security_level', value)}
                        className="mt-0.5 accent-[#003cc5]"
                      />
                      <div>
                        <span className="text-sm font-medium text-ink capitalize">{label}</span>
                        <p className="mt-0.5 text-xs text-neutral-500">{desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Narrative */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-neutral-600">
                  Narrative
                  {form.security_level === 'private' && (
                    <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700 border border-amber-200">
                      Will be AES-256-GCM encrypted
                    </span>
                  )}
                </label>
                <textarea
                  rows={4}
                  value={form.narrative}
                  onChange={(e) => setField('narrative', e.target.value)}
                  placeholder="Describe what happened — who was reached, context, outcomes…"
                  className="w-full resize-none rounded-lg border border-neutral-200 bg-parchment px-3 py-2 text-sm text-ink placeholder:text-neutral-400 focus:border-[#003cc5] focus:outline-none"
                />
                {form.security_level === 'private' && (
                  <p className="mt-1 text-xs text-amber-700">
                    Private narratives are encrypted at rest and only visible to authorised admins.
                  </p>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-neutral-600">
                  Personal testimony <span className="text-neutral-400">(optional)</span>
                </label>
                <textarea
                  rows={3}
                  value={form.story}
                  onChange={(e) => setField('story', e.target.value)}
                  placeholder="Share a personal reflection or testimony from this event…"
                  className="w-full resize-none rounded-lg border border-neutral-200 bg-parchment px-3 py-2 text-sm text-ink placeholder:text-neutral-400 focus:border-[#003cc5] focus:outline-none"
                />
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-3">
              <div className="rounded-xl border border-neutral-200 bg-parchment divide-y divide-neutral-200">
                {[
                  ['Coordinates', `${parseFloat(form.lat).toFixed(6)}, ${parseFloat(form.lng).toFixed(6)}`],
                  ['Region', form.region || '—'],
                  ['Impact count', form.impact_count],
                  ['Lives affected', form.lives_affected || '—'],
                  ['Event date', form.event_date],
                  ['Event type', form.event_type || '—'],
                  ['Security level', form.security_level],
                  ['Organisation', organisations.find((o) => String(o.id) === String(form.organization_id))?.label ?? form.organization_id],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between px-4 py-2.5 text-sm">
                    <span className="text-neutral-500">{label}</span>
                    <span className="text-right text-ink capitalize">{value}</span>
                  </div>
                ))}
              </div>

              {form.narrative && (
                <div className="rounded-xl border border-neutral-200 bg-parchment px-4 py-3 text-sm text-ink">
                  <p className="mb-1 text-xs text-neutral-500">Narrative</p>
                  {form.narrative}
                </div>
              )}

              {form.story && (
                <div className="rounded-xl border border-neutral-200 bg-parchment px-4 py-3 text-sm text-ink">
                  <p className="mb-1 text-xs text-neutral-500">Personal testimony</p>
                  {form.story}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────────── */}
        <div className="flex gap-3 border-t border-neutral-200 px-6 py-4 shrink-0">
          {step > 0 && (
            <button
              onClick={() => setStep(step - 1)}
              className="rounded-xl border border-neutral-200 bg-paper px-4 py-2.5 text-sm font-medium text-neutral-600 transition hover:bg-paper"
            >
              Back
            </button>
          )}

          {step < STEPS.length - 1 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="ml-auto rounded-xl bg-[#003cc5] px-6 py-2.5 text-sm font-semibold text-parchment transition hover:bg-[#0036b1] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitEvent.isPending}
              className="ml-auto rounded-xl bg-[#003cc5] px-6 py-2.5 text-sm font-semibold text-parchment transition hover:bg-[#0036b1] disabled:opacity-50"
            >
              {submitEvent.isPending ? 'Submitting…' : 'Submit Catalyst'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
